/**
 * Modal and drawer, ported from the prototype's `.modal-back` / `.modal` (and `.modal.drawer`,
 * used for the LinkedIn helper).
 *
 * The prototype's overlay is markup only. A real dialog needs the parts the prototype leaves
 * out, all of which are implemented here: `role="dialog"` with `aria-modal`, an accessible name
 * bound to the heading, focus moved into the dialog on open and restored to the invoker on
 * close, Tab cycling kept inside the dialog, Escape to dismiss and a body scroll lock.
 */
import { type KeyboardEvent, type ReactElement, type ReactNode, useEffect, useId, useRef } from 'react';
import { Button, IconButton } from './Button';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface ModalProps {
  title: ReactNode;
  children: ReactNode;
  /** Rendered in `.card-foot`; usually a cancel + confirm pair. */
  footer?: ReactNode;
  onClose: () => void;
  /** `wide` = prototype `.modal.wide`; `drawer` = right-hand `.modal.drawer`. */
  size?: 'default' | 'wide' | 'drawer';
  /** False keeps the dialog open on backdrop click and Escape (used while a command is running). */
  dismissible?: boolean;
  /** Extra description read after the title. */
  description?: ReactNode;
}

export function Modal({
  title,
  children,
  footer,
  onClose,
  size = 'default',
  dismissible = true,
  description,
}: ModalProps): ReactElement {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const restoreTo = useRef<Element | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    restoreTo.current = document.activeElement;
    const dialog = dialogRef.current;
    const first = dialog?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dialog)?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      const target = restoreTo.current;
      if (target instanceof HTMLElement && document.contains(target)) target.focus();
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape' && dismissible) {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (element) => element.offsetParent !== null || element === document.activeElement,
    );
    if (focusable.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];
    if (!firstElement || !lastElement) return;
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  return (
    // The backdrop is a pointer convenience duplicating the close button and the Escape key; it is not a control and must not enter the tab order.
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop duplicates the close button and Escape
    <div
      className={['modal-back', size === 'drawer' ? 'is-drawer' : ''].filter(Boolean).join(' ')}
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        className={['modal', size === 'wide' ? 'wide' : '', size === 'drawer' ? 'drawer' : '']
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="card-head">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? (
              <span className="small muted" id={descriptionId}>
                {description}
              </span>
            ) : null}
          </div>
          {dismissible ? <IconButton icon="x" label="Kapat" onClick={onClose} /> : null}
        </div>
        <div className="card-pad">{children}</div>
        {footer ? <div className="card-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
  /** Blocks confirm until the operator has supplied everything the command requires. */
  confirmDisabled?: boolean;
  size?: ModalProps['size'];
}

export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Vazgeç',
  onConfirm,
  onClose,
  busy = false,
  confirmDisabled = false,
  size = 'default',
}: ConfirmDialogProps): ReactElement {
  return (
    <Modal
      title={title}
      onClose={onClose}
      dismissible={!busy}
      size={size}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant="primary" onClick={onConfirm} busy={busy} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
